import React, { useState, useEffect, useRef } from "react";

/* A replay of two real workflow designs. The steps match the n8n builds; no customer data is shown. */
const FLOW_DATA = {
  listing: {
    title: "Rental listings to Facebook drafts",
    nodes: ["Trigger", "Rooms", "Caption", "Filter", "Save"],
    logs: [
      { text: "Trigger: daily schedule, or on demand from the staff form", delay: 200, type: "info" },
      { text: "Log in to the rental system and fetch rooms that are not posted yet", delay: 1000, type: "info" },
      { text: "Load room details and photos", delay: 1800, type: "info" },
      { text: "Gemini drafts one caption per room, following the posting rules", delay: 2600, type: "info" },
      { text: "Check each caption for banned words. Failures go back for a rewrite", delay: 4200, type: "warn" },
      { text: "Resize photos and save them to Google Drive", delay: 5000, type: "info" },
      { text: "Write ready-to-post rows to Google Sheets", delay: 5800, type: "info" },
      { text: "Done. Staff review each row and post by hand", delay: 6500, type: "success" },
    ],
  },
  invoice: {
    title: "Invoice follow-up (demo data)",
    nodes: ["Schedule", "Invoices", "Step", "Email", "Log"],
    logs: [
      { text: "Trigger: daily schedule", delay: 200, type: "info" },
      { text: "Fetch unpaid invoices from the CRM", delay: 1000, type: "info" },
      { text: "One invoice is past due. Pick the next reminder step", delay: 1800, type: "warn" },
      { text: "Choose the email template for that step", delay: 2600, type: "info" },
      { text: "Send the reminder over SMTP", delay: 4200, type: "info" },
      { text: "Skip invoices already marked paid by the payment webhook", delay: 5000, type: "info" },
      { text: "Log the result for the weekly revenue report", delay: 5800, type: "info" },
      { text: "Done. Demo run with sample data", delay: 6500, type: "success" },
    ],
  },
};

const NODE_TIMINGS = [0, 1000, 2600, 4200, 5800];

export const N8nSimulator = () => {
  const [activeFlow, setActiveFlow] = useState("listing");
  const [isRunning, setIsRunning] = useState(false);
  const [activeNode, setActiveNode] = useState(-1);
  const [logs, setLogs] = useState([]);
  const terminalBodyRef = useRef(null);
  const timeoutsRef = useRef([]);

  const stopSimulation = () => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    setIsRunning(false);
    setActiveNode(-1);
  };

  const runSimulation = (flowType = activeFlow) => {
    stopSimulation();
    setIsRunning(true);
    setLogs([]);
    setActiveNode(0);

    const flow = FLOW_DATA[flowType];

    NODE_TIMINGS.forEach((time, node) => {
      timeoutsRef.current.push(setTimeout(() => setActiveNode(node), time));
    });

    flow.logs.forEach((log) => {
      timeoutsRef.current.push(setTimeout(() => setLogs((prev) => [...prev, log]), log.delay));
    });

    const totalDuration = flow.logs[flow.logs.length - 1].delay + 800;
    timeoutsRef.current.push(
      setTimeout(() => {
        setIsRunning(false);
        setActiveNode(-1);
      }, totalDuration)
    );
  };

  useEffect(() => {
    return () => timeoutsRef.current.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (terminalBodyRef.current) {
      terminalBodyRef.current.scrollTop = terminalBodyRef.current.scrollHeight;
    }
  }, [logs]);

  const toggleFlow = (flowType) => {
    setActiveFlow(flowType);
    if (isRunning) {
      runSimulation(flowType);
    } else {
      setLogs([]);
      setActiveNode(-1);
    }
  };

  const currentNodes = FLOW_DATA[activeFlow].nodes;

  return (
    <div className="n8n-simulator-wrapper">
      <div className="n8n-header">
        <div className="n8n-brand">
          <span className="n8n-title">Workflow replay</span>
        </div>
        <div className="n8n-flow-tabs" role="group" aria-label="Choose a workflow">
          <button
            type="button"
            className={`n8n-tab-btn ${activeFlow === "listing" ? "active" : ""}`}
            aria-pressed={activeFlow === "listing"}
            onClick={() => toggleFlow("listing")}
          >
            Rental listings
          </button>
          <button
            type="button"
            className={`n8n-tab-btn ${activeFlow === "invoice" ? "active" : ""}`}
            aria-pressed={activeFlow === "invoice"}
            onClick={() => toggleFlow("invoice")}
          >
            Invoice follow-up
          </button>
        </div>
      </div>

      <div className="n8n-canvas">
        <svg className="n8n-svg-lines" viewBox="0 0 500 80" aria-hidden="true">
          <path
            d="M 50 40 L 150 40 L 250 40 L 350 40 L 450 40"
            fill="none"
            stroke="var(--border-2)"
            strokeWidth="3"
            strokeDasharray="6,6"
          />
          {isRunning && activeNode >= 0 && (
            <path
              d={`M 50 40 L ${50 + activeNode * 100} 40`}
              fill="none"
              stroke="var(--accent)"
              strokeWidth="3"
              className="n8n-pulse-line"
            />
          )}
        </svg>

        <ol className="n8n-nodes-row" aria-label={FLOW_DATA[activeFlow].title}>
          {currentNodes.map((node, index) => {
            const isNodeActive = isRunning && activeNode === index;
            const isNodePassed = isRunning && activeNode > index;

            return (
              <li
                key={node}
                className={`n8n-node-card ${isNodeActive ? "active" : ""} ${isNodePassed ? "passed" : ""}`}
              >
                <div className="n8n-node-icon-wrap">
                  <span className="n8n-icon" aria-hidden="true">{index + 1}</span>
                </div>
                <div className="n8n-node-label">{node}</div>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="n8n-terminal">
        <div className="terminal-header">
          <div className="terminal-title">{FLOW_DATA[activeFlow].title}</div>
        </div>
        <div className="terminal-body" ref={terminalBodyRef} aria-live="polite">
          {logs.length === 0 ? (
            <div className="terminal-idle">
              <span className="terminal-prompt">$</span> Run the workflow to see each step.
            </div>
          ) : (
            logs.map((log, index) => (
              <div key={index} className={`terminal-line ${log.type}`}>
                <span className="terminal-text">{log.text}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="n8n-controls">
        {!isRunning ? (
          <button type="button" className="n8n-btn run" onClick={() => runSimulation()}>
            Run workflow
          </button>
        ) : (
          <button type="button" className="n8n-btn stop" onClick={stopSimulation}>
            Stop
          </button>
        )}
      </div>
    </div>
  );
};
